import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Send, Calendar, ArrowRight, Loader2, X, Command } from 'lucide-react';
import { useGlobalSearch, type SearchResult, type SearchResultType } from '../hooks/useGlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';

// ── Types ──────────────────────────────────────────────────────────────────────

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

// ── Config ─────────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<SearchResultType, { label: string; icon: React.ElementType; color: string }> = {
  contact:  { label: 'Contatos',  icon: Users,    color: 'text-blue-300'    },
  campaign: { label: 'Campanhas', icon: Send,     color: 'text-fuchsia-300' },
  event:    { label: 'Agenda',    icon: Calendar, color: 'text-amber-300'   },
};

const SHORTCUTS = [
  { label: 'Contatos',   href: '/app/contatos',    icon: Users,    hint: 'C' },
  { label: 'Campanhas',  href: '/app/campanhas',   icon: Send,     hint: 'P' },
  { label: 'Agenda',     href: '/app/eventos',     icon: Calendar, hint: 'E' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery]       = useState('');
  const [activeIdx, setActive]  = useState(0);
  const inputRef  = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLDivElement>(null);
  const navigate  = useNavigate();

  const debouncedQuery = useDebounce(query, 200);
  const { data: results = [], isFetching } = useGlobalSearch(
    debouncedQuery,
    open && debouncedQuery.length >= 1,
  );

  // ── Group results ────────────────────────────────────────────────────────────

  const grouped = groupResults(results);
  const flat    = results; // flat list for keyboard nav

  // ── Reset on open ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ── Keep activeIdx in range ──────────────────────────────────────────────────

  useEffect(() => {
    setActive(0);
  }, [debouncedQuery]);

  // ── Scroll active item into view ─────────────────────────────────────────────

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  // ── Navigate to result ───────────────────────────────────────────────────────

  const goTo = useCallback((href: string) => {
    onClose();
    navigate(href);
  }, [navigate, onClose]);

  // ── Keyboard handler ─────────────────────────────────────────────────────────

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { onClose(); return; }

    const items = query.trim() ? flat : SHORTCUTS.map((s) => ({ id: s.label, href: s.href }));

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[activeIdx];
      if (item) goTo(item.href);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl rounded-2xl card-premium shadow-deep overflow-hidden animate-scale-in">

        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-hairline">
          <Search className="h-4 w-4 shrink-0 text-ink-subtle" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Buscar contatos, campanhas, eventos…"
            className="flex-1 bg-transparent text-sm text-ink placeholder-ink-faint focus:outline-none"
          />
          <div className="flex items-center gap-2 shrink-0">
            {isFetching && <Loader2 className="h-3.5 w-3.5 text-ink-subtle animate-spin" />}
            {query && (
              <button
                onClick={() => setQuery('')}
                className="rounded-md p-0.5 text-ink-subtle hover:text-ink transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <kbd className="rounded border border-hairline px-1.5 py-0.5 font-mono text-[10px] text-ink-faint">
              ESC
            </kbd>
          </div>
        </div>

        {/* Results / suggestions */}
        <div ref={listRef} className="max-h-[420px] overflow-y-auto py-2">

          {/* No query — shortcuts */}
          {!query.trim() && (
            <div className="px-3">
              <p className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-ink-subtle">
                Atalhos rápidos
              </p>
              {SHORTCUTS.map((s, i) => {
                const cfg = TYPE_CONFIG[s.href.includes('contatos')
                  ? 'contact' : s.href.includes('campanhas')
                  ? 'campaign' : 'event'];
                const Icon = cfg.icon;
                return (
                  <button
                    key={s.label}
                    data-active={activeIdx === i ? 'true' : undefined}
                    onClick={() => goTo(s.href)}
                    onMouseEnter={() => setActive(i)}
                    className={`group w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                      activeIdx === i ? 'bg-accent/10' : 'hover:bg-white/[0.04]'
                    }`}
                  >
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-midnight-4 ${cfg.color}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1 text-sm font-medium text-ink">{s.label}</span>
                    <kbd className="rounded border border-hairline px-1.5 py-0.5 font-mono text-[10px] text-ink-faint">
                      {s.hint}
                    </kbd>
                    <ArrowRight className="h-3.5 w-3.5 text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                );
              })}

              <div className="mt-3 px-2 pb-3 flex items-center gap-2 text-[10px] text-ink-faint">
                <Command className="h-3 w-3" />
                <span>Use ↑↓ para navegar · Enter para abrir · Esc para fechar</span>
              </div>
            </div>
          )}

          {/* Has query — results */}
          {query.trim().length >= 1 && (
            <>
              {results.length === 0 && !isFetching && (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                  <Search className="h-8 w-8 text-ink-subtle opacity-40" />
                  <p className="text-sm text-ink-muted">
                    Nenhum resultado para{' '}
                    <span className="font-semibold text-ink">"{query}"</span>
                  </p>
                </div>
              )}

              {grouped.map(({ type, items }) => {
                const cfg = TYPE_CONFIG[type];
                const Icon = cfg.icon;

                return (
                  <div key={type} className="px-3 py-1">
                    <div className="flex items-center gap-2 px-2 py-1.5">
                      <Icon className={`h-3 w-3 ${cfg.color}`} />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-ink-subtle">
                        {cfg.label}
                      </span>
                    </div>

                    {items.map((item) => {
                      const globalIdx = flat.findIndex((r) => r.id === item.id);
                      return (
                        <ResultRow
                          key={item.id}
                          result={item}
                          active={activeIdx === globalIdx}
                          onHover={() => setActive(globalIdx)}
                          onClick={() => goTo(item.href)}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Result row ─────────────────────────────────────────────────────────────────

interface ResultRowProps {
  result: SearchResult;
  active: boolean;
  onHover: () => void;
  onClick: () => void;
}

function ResultRow({ result, active, onHover, onClick }: ResultRowProps) {
  return (
    <button
      data-active={active ? 'true' : undefined}
      onClick={onClick}
      onMouseEnter={onHover}
      className={`group w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
        active ? 'bg-accent/10' : 'hover:bg-white/[0.04]'
      }`}
    >
      {/* Initial avatar */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent text-xs font-bold">
        {result.title[0].toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink truncate">{result.title}</p>
        <p className="text-[11px] text-ink-subtle truncate">{result.subtitle}</p>
      </div>

      {result.meta && (
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${result.metaColor}`}>
          {result.meta}
        </span>
      )}

      <ArrowRight className="h-3.5 w-3.5 text-ink-faint shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function groupResults(results: SearchResult[]) {
  const order: SearchResultType[] = ['contact', 'campaign', 'event'];
  return order
    .map((type) => ({
      type,
      items: results.filter((r) => r.type === type),
    }))
    .filter((g) => g.items.length > 0);
}
